import { useState, type FormEvent } from "react";
import { site } from "@/mocks/hotlist";

const FORM_ID = "heiliaohome-contact-form";
const SUBMIT_ADDR = "https://readdy.ai/api/form/daij6lboh653ivfvoe50";

type Status = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const CONTACT_ITEMS = [
  { icon: "ri-mail-line", label: "内容与投稿邮箱", value: site.email, tone: "primary" },
  { icon: "ri-wechat-line", label: "微信联系", value: site.wechat, tone: "secondary" },
  { icon: "ri-timer-line", label: "一般回复时间", value: "工作日 24 小时内", tone: "accent" },
];

const TONE: Record<string, string> = {
  primary: "text-primary-400",
  secondary: "text-secondary-400",
  accent: "text-accent-400",
};

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, typeof value === "string" ? value : "");
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const messageText = typeof serverMsg === "string" ? serverMsg : "";
      const isSpam = messageText.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(messageText || "提交未成功，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络连接后重试。");
    }
  };

  return (
    <section id="contact" className="w-full py-16 md:py-24 bg-background-100 border-t border-background-200/70">
      <div className="w-full px-4 md:px-8">
        <div className="w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs md:text-sm text-secondary-400 font-medium">
              <span className="w-6 h-px bg-secondary-500" />
              联系方式
            </span>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-snug">
              有话题线索或更正意见，直接告诉我们
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              如果你发现站内信息有误，或想推荐一个正在起量的话题，可以填写右侧表单，也可以直接通过下面的方式联系。
            </p>

            <ul className="mt-8 space-y-4">
              {CONTACT_ITEMS.map((item) => (
                <li key={item.label} className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-lg bg-background-50 border border-background-200/70 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-lg ${TONE[item.tone]}`} />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">{item.label}</span>
                    <span className="block text-sm md:text-base text-foreground-900">{item.value}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <form
              id={FORM_ID}
              data-readdy-form
              action={SUBMIT_ADDR}
              method="post"
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-7"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-2">
                    怎么称呼你
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="例如：小满"
                    className="w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/70 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-2">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/70 transition-colors"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-category" className="block text-sm text-foreground-700 mb-2">
                  反馈类型
                </label>
                <select
                  id="contact-category"
                  name="category"
                  required
                  defaultValue="话题线索"
                  className="w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-500/70 transition-colors cursor-pointer"
                >
                  <option value="话题线索">话题线索推荐</option>
                  <option value="内容更正">内容更正</option>
                  <option value="合作洽谈">合作洽谈</option>
                  <option value="其他建议">其他建议</option>
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-2">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请简单描述你推荐的话题或需要更正的内容，最多 500 字。"
                  className="w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/70 transition-colors resize-none"
                />
              </div>

              <div className="form-extra-check" aria-hidden="true">
                <label htmlFor="contact-website-alt">请勿填写此栏</label>
                <input
                  id="contact-website-alt"
                  name="website_alt"
                  type="text"
                  className="form-extra-check"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"} />
                  {status === "submitting" ? "正在提交" : "提交留言"}
                </button>
                <p className="text-xs text-foreground-500">提交即表示你同意我们仅将该信息用于话题跟进。</p>
              </div>

              {status === "success" && (
                <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-md bg-secondary-500/10 border border-secondary-500/30 animate-fade-in">
                  <i className="ri-checkbox-circle-line text-secondary-400" />
                  <span className="text-sm text-secondary-300">
                    已收到你的留言，我们会在工作日 24 小时内查看。
                  </span>
                </div>
              )}

              {status === "error" && (
                <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-md bg-accent-500/10 border border-accent-500/30 animate-fade-in">
                  <i className="ri-error-warning-line text-accent-400" />
                  <span className="text-sm text-accent-300">
                    {formError || "提交未成功，请稍后再试。"}
                  </span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}